import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { rehydrateMapsCache, persistMapsCache } from './mapCachePersister'
import { reportError } from './reporting'

// Global error capture: EVERY query and mutation failure flows through here,
// so a rejected request can never be silently swallowed by a component that
// forgot an onError (the class of bug that hid the Android Fortify 415). The
// reportError filter drops expected statuses (401/403/404/409/422) to avoid
// noise; per-call onError handlers still run for user-facing messaging.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) =>
      reportError(error, { tags: { layer: 'query' }, extra: { queryKey: query.queryKey } }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      reportError(error, {
        tags: { layer: 'mutation' },
        extra: { mutationKey: mutation.options.mutationKey ?? null },
      }),
  }),
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
      gcTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: 0,
    },
  },
})

// ── ['maps', …] cache persistence (Tier 1 #2) ────────────────────────────────
// Rehydrate any previously-persisted maps data from storage before the first
// component renders (web: localStorage; native: no-op, bundled assets cover
// the cold-start gap instead). Then subscribe to cache mutations and write
// only when a ['maps', …] key changes.
//
// The persister is scoped to ['maps', …] ONLY. Game state, auth tokens, and
// user data are never touched.
//
// See lib/mapCachePersister.ts for the implementation notes and the rationale
// for not using @tanstack/react-query-persist-client + MMKV at this stage
// (native build constraint).
rehydrateMapsCache(queryClient)

queryClient.getQueryCache().subscribe((event) => {
  // Only write to storage when a maps cache entry is updated (new data
  // arrived from the network). Skip observer / remove events that don't
  // change the persisted set — particularly important to avoid a
  // synchronous localStorage write on every render.
  if (
    event.type === 'updated' &&
    Array.isArray(event.query.queryKey) &&
    event.query.queryKey[0] === 'maps'
  ) {
    persistMapsCache(queryClient)
  }
})
